import React, { useContext, useMemo, useState, useEffect } from 'react';
//mui imports====
import useStyles from './styles';
import { Box } from '@mui/system';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { UserContext } from '../../Contexts/UserContext';
import { ACCESS_CONTROL_CONSTANTS } from '../../core/constants';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
//===============

const { SYSTEMS, UI_ACCESS, TYPES } = ACCESS_CONTROL_CONSTANTS;

/**
 * Checks if any child setting (recursively) matches the current setting.
 */
const hasChildSelected = (settings, currentSetting) => {
	if (!currentSetting || !Array.isArray(settings)) return false;
	return settings.some((s) => {
		if (s.settingName === currentSetting) return true;
		if (s.isAccordion && s.settings) return hasChildSelected(s.settings, currentSetting);
		return false;
	});
};

const AccordionSettings = ({
	icon = null,
	sidebarCollapsed = false,
	sidebarOptionHovered = false,
	label,
	children,
	accordionStyles = {},
	accordionDetailStyles = {},
	name,
	system = SYSTEMS.FRAMERATE,
	currentSetting,
	childSettings
}) => {
	const classes = useStyles();
	const { getAccessControlSet } = useContext(UserContext);
	const hasSidebarCollapsed = sidebarCollapsed && !sidebarOptionHovered;

	const childIsSelected = useMemo(
		() => hasChildSelected(childSettings, currentSetting),
		[childSettings, currentSetting]
	);

	const [expanded, setExpanded] = useState(childIsSelected);

	// Sync expansion state with whether a child is selected.
	// This ensures that when navigating to another tab, this accordion collapses if no longer active.
	useEffect(() => {
		setExpanded(childIsSelected);
	}, [childIsSelected]);

	const accordionStylesClasses = hasSidebarCollapsed
		? `${classes.accordionStyle} ${classes.accordionStyleCollapsed}`
		: classes.accordionStyle;
	const accordionDetailsClasses = hasSidebarCollapsed
		? `${classes.accordionDetails} ${classes.accordionDetailsCollapsed}`
		: classes.accordionDetails;

	const accordionLabelContainerClasses = hasSidebarCollapsed
		? `${classes.accordionLabelContainer} ${classes.accordionLabelContainerCollapsed}`
		: classes.accordionLabelContainer;

	const accordionLabelIconContainerClasses = hasSidebarCollapsed
		? `${classes.accordionLabelIconContainer} ${classes.accordionLabelIconContainerCollapsed}`
		: classes.accordionLabelIconContainer;

	const accordionLabelNameClasses = hasSidebarCollapsed
		? classes.accordionLabelNameCollapsed
		: classes.accordionLabelNameExpanded;

	const accordionExpandIconClasses = hasSidebarCollapsed ? classes.acordionExpandIconCollapsed : '';

	/**
	 * This set contains the access level of this particular accordion setting.
	 * @type {Set<string>}
	 */
	const accordionControlledAccessSet = useMemo(() => {
		return getAccessControlSet(system, TYPES.ACCORDION_NAMES, name);
	}, [name, getAccessControlSet, system]);

	return (
		<>
			{!accordionControlledAccessSet.has(UI_ACCESS.HIDE) && (
				<Accordion
					className={accordionStylesClasses}
					style={accordionStyles}
					expanded={expanded}
					onChange={(_, isExpanded) => setExpanded(isExpanded)}
				>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon className={accordionExpandIconClasses} />}
						aria-controls="panel1a-content"
						id="panel1a-header"
						style={{ padding: '0', paddingLeft: '1.2rem', paddingRight: '.8rem' }}
					>
						<Box className={accordionLabelContainerClasses}>
							<Box className={accordionLabelIconContainerClasses}>
								{hasSidebarCollapsed ? (
									<>{icon ? icon : <FiberManualRecordIcon fontSize="small" data-testid="fiber-dot-icon" />}</>
								) : (
									<>{icon}</>
								)}
							</Box>

							<Typography variant="h6" className={accordionLabelNameClasses}>
								{label}
							</Typography>
						</Box>
					</AccordionSummary>
					<AccordionDetails className={accordionDetailsClasses} style={accordionDetailStyles}>
						{children}
					</AccordionDetails>
				</Accordion>
			)}
		</>
	);
};
export default AccordionSettings;

